import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CalendarMonth as CalendarMonthIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import CoachAvailabilityCalendar from './CoachAvailabilityCalendar';
import CoachAvailabilityWeeklyView from './CoachAvailabilityWeeklyView';
import CoachAvailabilityDailyTimeline from './CoachAvailabilityDailyTimeline';
import { authService } from '../../services/authService';
import { getTodayLocal } from '../../utils/dateUtils';

type CoachAvailabilityView = 'monthly' | 'weekly' | 'daily';

const CoachAvailability: React.FC = () => {
  const [view, setView] = useState<CoachAvailabilityView>('monthly');
  const [selectedDate, setSelectedDate] = useState<string>(getTodayLocal());
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    const loadUserRole = () => {
      try {
        const currentUser = authService.getCurrentUser();
        setUserRole(currentUser?.userRole || '');
      } catch (error) {
        console.error('Error loading user role:', error);
      }
    };

    loadUserRole();
  }, []);

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: CoachAvailabilityView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
  };

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, overflow: 'hidden' }}>
      <Box>
        {/* View Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Paper sx={{ p: 0.5, alignSelf: { xs: 'center', sm: 'auto' } }}>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={handleViewChange}
              aria-label="coach availability view"
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  px: { xs: 0.75, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                  minWidth: { xs: 'auto', sm: 'unset' },
                  flexShrink: 1
                }
              }}
            >
              <ToggleButton value="monthly" aria-label="monthly view">
                <CalendarMonthIcon sx={{ mr: { xs: 0.25, sm: 1 }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Havi nézet</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Havi</Box>
              </ToggleButton>
              <ToggleButton value="weekly" aria-label="weekly view">
                <DateRangeIcon sx={{ mr: { xs: 0.25, sm: 1 }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Heti nézet</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Heti</Box>
              </ToggleButton>
              <ToggleButton value="daily" aria-label="daily view">
                <ScheduleIcon sx={{ mr: { xs: 0.25, sm: 1 }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }} />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Napi nézet</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Napi</Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Paper>
        </Box>

        {/* Conditional View Rendering */}
        {view === 'monthly' && (
          <CoachAvailabilityCalendar
            onDateSelect={handleDateSelect}
            selectedDate={selectedDate}
            userRole={userRole}
          />
        )}

        {view === 'weekly' && (
          <CoachAvailabilityWeeklyView
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            userRole={userRole}
          />
        )}

        {view === 'daily' && (
          <CoachAvailabilityDailyTimeline
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            userRole={userRole}
          />
        )}
      </Box>
    </Box>
  );
};

export default CoachAvailability;